/**
 * Мини-карта одного дрона для режима сетки.
 *
 * Намеренно НЕ экземпляр Leaflet. Двадцать полноценных карт — это двадцать
 * наборов обработчиков, панелей и слоёв тайлов, каждый со своей логикой
 * подгрузки; вкладка этого не переживёт. Плитке интерактивность не нужна:
 * её задача — показать, где аппарат и куда смотрит. Поэтому тайлы кладутся
 * обычными <img>, а иконка позиционируется поверх.
 */

import './DroneMiniMap.css';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import CopterIcon from '../../assets/icons/copter.svg';
import CopterOfflineIcon from '../../assets/icons/copter_offline.svg';
import PlaneIcon from '../../assets/icons/plane.svg';
import PlaneOfflineIcon from '../../assets/icons/plane_offline.svg';
import { isPlane } from '../../utils/mavMode';

const TILE_SIZE = 256;
const MINI_ZOOM = 15;
// Пока дрон не отошёл от якоря дальше этого, набор тайлов не пересчитываем:
// иначе каждый пакет телеметрии менял бы src у всех картинок.
const RECENTER_PX = 40;

interface WorldPoint {
    x: number;
    y: number;
}

function worldPoint(lat: number, lng: number, zoom: number): WorldPoint {
    const scale = Math.pow(2, zoom);
    // Меркатор не определён у полюсов: на |lat| около 90 тангенс уходит в
    // бесконечность и координата становится NaN. Отсекаем по границе,
    // на которой проекция обрывается у всех тайловых сервисов.
    const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
    const rad = (clamped * Math.PI) / 180;
    const worldSize = scale * TILE_SIZE;
    const y = ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * worldSize;

    return {
        x: ((lng + 180) / 360) * worldSize,
        // Ровно на границе отсечения round-off уводит y в малый минус, и
        // floor(y / 256) даёт тайл -1, которого не существует. Прижимаем к
        // допустимому диапазону, чтобы индекс всегда был валиден.
        y: Math.max(0, Math.min(worldSize - 1, y))
    };
}

function tileUrl(x: number, y: number, z: number): string {
    // Те же тайлы, что у большой карты (maps/GoogleMap.tsx), но по https:
    // на странице по https обычный http дал бы смешанный контент.
    const sub = Math.abs(x + y) % 4;

    return `https://mt${sub}.google.com/vt/lyrs=s,p&x=${x}&y=${y}&z=${z}`;
}

interface DroneMiniMapProps {
    lat: number | null;
    lng: number | null;
    yaw: number;
    isOnline: boolean;
    mavType: number;
    /** Текст, когда позиции ещё нет. */
    noPositionLabel: string;
}

function DroneMiniMap({ lat, lng, yaw, isOnline, mavType, noPositionLabel }: DroneMiniMapProps): ReactNode {
    const boxRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    // Точка, вокруг которой разложены тайлы. Отстаёт от дрона намеренно.
    const [anchor, setAnchor] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        const node = boxRef.current;
        if (!node) {
            return;
        }
        const observer = new ResizeObserver(() => {
            setSize({ w: node.clientWidth, h: node.clientHeight });
        });
        observer.observe(node);
        setSize({ w: node.clientWidth, h: node.clientHeight });

        return () => observer.disconnect();
    }, []);

    const hasPosition = lat != null && lng != null;

    useEffect(() => {
        if (!hasPosition) {
            return;
        }
        if (!anchor) {
            setAnchor({ lat: lat as number, lng: lng as number });

            return;
        }
        const a = worldPoint(anchor.lat, anchor.lng, MINI_ZOOM);
        const d = worldPoint(lat as number, lng as number, MINI_ZOOM);
        if (Math.hypot(d.x - a.x, d.y - a.y) > RECENTER_PX) {
            setAnchor({ lat: lat as number, lng: lng as number });
        }
    }, [lat, lng, hasPosition, anchor]);

    const layout = useMemo(() => {
        if (!anchor || size.w === 0 || size.h === 0 || !hasPosition) {
            return null;
        }
        const a = worldPoint(anchor.lat, anchor.lng, MINI_ZOOM);
        const originX = a.x - size.w / 2;
        const originY = a.y - size.h / 2;

        const tiles: { key: string; url: string; left: number; top: number }[] = [];
        const maxIndex = Math.pow(2, MINI_ZOOM) - 1;
        for (let tx = Math.floor(originX / TILE_SIZE); tx <= Math.floor((originX + size.w) / TILE_SIZE); tx++) {
            for (let ty = Math.floor(originY / TILE_SIZE); ty <= Math.floor((originY + size.h) / TILE_SIZE); ty++) {
                if (ty < 0 || ty > maxIndex) {
                    continue;
                }
                // По долготе мир заворачивается: -1 — это последний тайл.
                const wrappedX = ((tx % (maxIndex + 1)) + maxIndex + 1) % (maxIndex + 1);
                tiles.push({
                    key: `${tx}_${ty}`,
                    url: tileUrl(wrappedX, ty, MINI_ZOOM),
                    left: tx * TILE_SIZE - originX,
                    top: ty * TILE_SIZE - originY
                });
            }
        }

        const d = worldPoint(lat as number, lng as number, MINI_ZOOM);

        return { tiles, droneX: d.x - originX, droneY: d.y - originY };
    }, [anchor, size, lat, lng, hasPosition]);

    const iconUrl = isPlane(mavType)
        ? (isOnline ? PlaneIcon : PlaneOfflineIcon)
        : (isOnline ? CopterIcon : CopterOfflineIcon);

    return (
        <div className='drone-mini-map' ref={boxRef}>
            {layout ? (
                <>
                    {layout.tiles.map((tile) => (
                        <img
                            key={tile.key}
                            className='drone-mini-map-tile'
                            src={tile.url}
                            alt=''
                            loading='lazy'
                            draggable={false}
                            style={{ left: tile.left, top: tile.top }}
                        />
                    ))}
                    <img
                        className='drone-mini-map-icon'
                        src={iconUrl}
                        alt=''
                        draggable={false}
                        style={{
                            left: layout.droneX,
                            top: layout.droneY,
                            transform: `translate(-50%, -50%) rotate(${-yaw}deg)`
                        }}
                    />
                </>
            ) : (
                <div className='drone-mini-map-empty'>{noPositionLabel}</div>
            )}
        </div>
    );
}

export default DroneMiniMap;
